'use client'

import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import styles from './LandingHeroMedia.module.css'

// A single pass through the reviewed, forward-only clips. No looping or
// reverse playback. Buffer the next clip only near the active clip's end.
export const landingHeroSlides = [
  {
    id: 'flower',
    src: '/assets/landing-polish/studio-brunette-v1.webp',
    alt: 'AI-created brunette in a coral tank beside a flower market',
    videoSrc: '/assets/landing-polish/hero-flower-brunette-v1.mp4',
  },
  {
    id: 'blonde',
    src: '/assets/landing-explore-v2/boardwalk-blonde-v2.webp',
    alt: 'AI-created blonde in a white tank looking back on a seaside boardwalk',
    videoSrc: '/assets/landing-feed-motion-v1/boardwalk-blonde-v2.mp4',
  },
  {
    id: 'arcade',
    src: '/assets/landing-polish/studio-pink-3d-v2.webp',
    alt: 'AI-created pink-haired character in a mint T-shirt at a softly lit arcade',
    videoSrc: '/assets/landing-polish/hero-arcade-pink-v1.mp4',
  },
]

const sizes = '(max-width: 700px) 100vw, (max-width: 1200px) 60vw, 720px'
const fadeDuration = 1200
const fallbackDwell = 5000

const initialPlaylist = {
  activeIndex: 0,
  outgoingIndex: null,
  stage: 'waiting',
  requested: [0],
  decoded: [],
  posters: {},
  mediaReady: [],
  mediaFailed: [],
  failed: [],
  generation: 0,
}

function playlistReducer(state, action) {
  // The primary still remains mounted during preference changes. Every other
  // poster is remounted, so its old decode callback must not mark it ready.
  if (action.type === 'reset')
    return {
      ...initialPlaylist,
      decoded: state.decoded.filter((index) => index === 0),
      posters: state.posters[0] ? { 0: state.posters[0] } : {},
      failed: state.failed.filter((index) => index === 0),
      generation: state.generation + 1,
    }
  const primaryImageEvent =
    action.index === 0 && ['decoded', 'failed'].includes(action.type)
  if (action.generation !== state.generation && !primaryImageEvent) return state

  switch (action.type) {
    case 'stage':
      if (
        action.activeIndex !== state.activeIndex ||
        state.stage === action.stage
      )
        return state
      if (
        ['rotate', 'ended'].includes(state.stage) &&
        action.stage === 'prepare'
      )
        return state
      return { ...state, stage: action.stage }
    case 'media-ready':
      return state.mediaReady.includes(action.index)
        ? state
        : { ...state, mediaReady: [...state.mediaReady, action.index] }
    case 'media-failed':
      return state.mediaFailed.includes(action.index)
        ? state
        : { ...state, mediaFailed: [...state.mediaFailed, action.index] }
    case 'autoplay-blocked':
      return {
        ...state,
        mediaFailed: Array.from(
          { length: action.slideCount },
          (_, index) => index,
        ),
      }
    case 'request':
      return state.requested.includes(action.index)
        ? state
        : { ...state, requested: [...state.requested, action.index] }
    case 'decoded':
      return state.decoded.includes(action.index) ||
        state.failed.includes(action.index)
        ? state
        : {
            ...state,
            decoded: [...state.decoded, action.index],
            posters: { ...state.posters, [action.index]: action.src },
          }
    case 'failed':
      return state.failed.includes(action.index)
        ? state
        : {
            ...state,
            failed: [...state.failed, action.index],
            decoded: state.decoded.filter((index) => index !== action.index),
          }
    case 'advance':
      if (
        !['rotate', 'ended'].includes(state.stage) ||
        !state.decoded.includes(action.index) ||
        state.failed.includes(action.index)
      )
        return state
      return {
        ...state,
        outgoingIndex: state.activeIndex,
        activeIndex: action.index,
        stage: 'waiting',
      }
    case 'release':
      return state.outgoingIndex === action.index
        ? { ...state, outgoingIndex: null }
        : state
    default:
      return state
  }
}

function nextAvailableIndex(activeIndex, failed, slideCount) {
  for (let index = activeIndex + 1; index < slideCount; index += 1) {
    if (!failed.includes(index)) return index
  }
  return null
}

function MotionPortrait({
  index,
  src,
  poster,
  running,
  prepare,
  visible,
  holdFrame,
  onStatus,
}) {
  const videoRef = useRef(null)
  const [requested, setRequested] = useState(false)
  const [failed, setFailed] = useState(false)
  const [ended, setEnded] = useState(false)

  // Source-free posters paint first. Buffer only the next clip once playback
  // is underway; it stays paused until the crossfade begins.
  useEffect(() => {
    if (running || prepare) setRequested(true)
  }, [running, prepare])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    return () => {
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
  }, [failed])

  useEffect(() => {
    const video = videoRef.current
    if (!video || !requested || failed || ended) return
    let cancelled = false
    if (running) {
      video.play().catch((error) => {
        if (!cancelled) {
          setFailed(true)
          onStatus({
            type:
              error.name === 'NotAllowedError'
                ? 'autoplay-blocked'
                : 'media-failed',
            index,
          })
        }
      })
    } else {
      video.pause()
    }
    return () => {
      cancelled = true
      video.pause()
    }
  }, [running, requested, failed, ended, onStatus, index])

  if (failed) return null
  // Reuse the decoded responsive still as the native poster before assigning
  // a video source. This keeps the first paint independent of clip loading.
  // Buffered clips stay hidden until the fade: opacity alone still lets some
  // browsers paint their first frame and replace the page's initial LCP.
  return (
    <video
      ref={videoRef}
      src={requested ? src : undefined}
      poster={poster}
      muted
      playsInline
      preload={requested ? 'auto' : 'none'}
      hidden={!visible}
      aria-hidden="true"
      tabIndex={-1}
      className={`${styles.video} ${!ended || holdFrame ? styles.playing : ''}`}
      data-hero-video={requested ? '' : undefined}
      onCanPlay={() => onStatus({ type: 'media-ready', index })}
      onTimeUpdate={(event) => {
        const video = event.currentTarget
        if (!running || !Number.isFinite(video.duration)) return
        if (video.duration - video.currentTime <= fadeDuration / 1000 + 0.2)
          onStatus({ type: 'stage', activeIndex: index, stage: 'rotate' })
        else if (video.currentTime >= Math.min(1.5, video.duration / 3))
          onStatus({ type: 'stage', activeIndex: index, stage: 'prepare' })
      }}
      onEnded={() => {
        setEnded(true)
        onStatus({ type: 'stage', activeIndex: index, stage: 'ended' })
      }}
      onError={() => {
        setFailed(true)
        onStatus({ type: 'media-failed', index })
      }}
    />
  )
}

function HeroSignupLink({ label }) {
  if (!label) return null
  // The motion control is a sibling above this link, never nested inside it.
  return (
    <Link
      href="/signup"
      prefetch={false}
      className={styles.signupLink}
      aria-label={label}
      data-landing-cta="hero-artwork"
      data-landing-action="hero_artwork_signup"
    />
  )
}

function RotatingHero({ slides, signupLinkLabel }) {
  const { t } = useTranslation()
  const containerRef = useRef(null)
  const [permitted, setPermitted] = useState(false)
  const [inView, setInView] = useState(false)
  const [pageVisible, setPageVisible] = useState(false)
  const [enhanced, setEnhanced] = useState(false)
  const [paused, setPaused] = useState(false)
  const [playlist, dispatch] = useReducer(playlistReducer, initialPlaylist)
  const {
    activeIndex,
    outgoingIndex,
    stage,
    requested,
    decoded,
    posters,
    mediaReady,
    mediaFailed,
    failed,
    generation,
  } = playlist
  const primaryDecoded = decoded.includes(0)
  const nextIndex = nextAvailableIndex(activeIndex, failed, slides.length)

  const completed = stage === 'ended' && nextIndex === null
  const running =
    permitted && enhanced && inView && pageVisible && !paused && !completed
  const handleMediaStatus = useCallback(
    (action) => dispatch({ ...action, generation, slideCount: slides.length }),
    [generation, slides.length],
  )

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)')
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection
    const updatePreference = () => {
      const constrained =
        connection?.saveData ||
        ['slow-2g', '2g', '3g'].includes(connection?.effectiveType)
      const allowed = !motion.matches && !constrained
      setPermitted(allowed)
      if (!allowed) {
        dispatch({ type: 'reset' })
        setEnhanced(false)
      }
    }
    const updateVisibility = () =>
      setPageVisible(document.visibilityState === 'visible')
    updatePreference()
    updateVisibility()
    motion.addEventListener('change', updatePreference)
    connection?.addEventListener?.('change', updatePreference)
    document.addEventListener('visibilitychange', updateVisibility)
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.05 },
    )
    observer.observe(containerRef.current)
    return () => {
      motion.removeEventListener('change', updatePreference)
      connection?.removeEventListener?.('change', updatePreference)
      document.removeEventListener('visibilitychange', updateVisibility)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (
      !permitted ||
      !primaryDecoded ||
      !inView ||
      !pageVisible ||
      paused ||
      enhanced
    )
      return
    let delay
    let idle
    let cancelled = false
    const activate = () => {
      if (!cancelled) setEnhanced(true)
    }
    const afterLoad = () => {
      // The responsive hero still owns the initial load. Even a cached page
      // gets breathing room before requesting any decorative motion.
      delay = window.setTimeout(() => {
        if ('requestIdleCallback' in window)
          idle = window.requestIdleCallback(activate, { timeout: 2000 })
        else activate()
      }, 1500)
    }
    if (document.readyState === 'complete') afterLoad()
    else window.addEventListener('load', afterLoad, { once: true })
    return () => {
      cancelled = true
      window.clearTimeout(delay)
      if (idle !== undefined) window.cancelIdleCallback(idle)
      window.removeEventListener('load', afterLoad)
    }
  }, [permitted, primaryDecoded, inView, pageVisible, paused, enhanced])

  useEffect(() => {
    if (!running || !mediaFailed.includes(activeIndex)) return
    // If autoplay is blocked, preserve a calm still-to-still sequence. Normal
    // playback is driven by media time, so buffering never leaves a timed gap.
    dispatch({ type: 'stage', stage: 'prepare', activeIndex, generation })
    const fallback = window.setTimeout(
      () =>
        dispatch({ type: 'stage', stage: 'ended', activeIndex, generation }),
      fallbackDwell,
    )
    return () => {
      window.clearTimeout(fallback)
    }
  }, [running, activeIndex, mediaFailed, generation])

  useEffect(() => {
    if (outgoingIndex === null) return
    const release = window.setTimeout(
      () => dispatch({ type: 'release', index: outgoingIndex, generation }),
      fadeDuration,
    )
    return () => window.clearTimeout(release)
  }, [outgoingIndex, generation])

  useEffect(() => {
    if (
      running &&
      stage !== 'waiting' &&
      nextIndex !== null &&
      !requested.includes(nextIndex)
    ) {
      dispatch({ type: 'request', index: nextIndex, generation })
    }
  }, [running, stage, nextIndex, requested, generation])

  useEffect(() => {
    // A failed destination advances the candidate, not the visible portrait.
    // Late decodes can update readiness while paused but cannot change slides.
    if (
      running &&
      ['rotate', 'ended'].includes(stage) &&
      nextIndex !== null &&
      decoded.includes(nextIndex) &&
      (stage === 'ended' ||
        mediaReady.includes(nextIndex) ||
        mediaFailed.includes(nextIndex))
    ) {
      dispatch({ type: 'advance', index: nextIndex, generation })
    }
  }, [running, stage, nextIndex, decoded, mediaReady, mediaFailed, generation])

  const phase = !permitted
    ? 'static'
    : completed
      ? 'complete'
      : paused
        ? 'paused'
        : !enhanced
          ? 'deferred'
          : running
            ? 'running'
            : 'suspended'

  return (
    <div
      ref={containerRef}
      className={styles.media}
      data-hero-media
      data-hero-phase={phase}
      data-hero-active={slides[activeIndex].id}
    >
      {slides.map((slide, index) => {
        if (index > 0 && (!permitted || !requested.includes(index))) return null
        return (
          <div
            key={slide.id}
            className={`${styles.slide} ${index === activeIndex ? styles.active : ''} ${index === outgoingIndex ? styles.outgoing : ''}`}
            aria-hidden={index !== activeIndex}
            data-hero-slide={slide.id}
          >
            <Image
              src={slide.src}
              alt={
                slide.altKey
                  ? t(slide.altKey, { defaultValue: slide.alt })
                  : slide.alt
              }
              fill
              priority={index === 0}
              loading={index === 0 ? undefined : 'eager'}
              sizes={sizes}
              onLoad={async (event) => {
                const image = event.currentTarget
                try {
                  await image.decode()
                  dispatch({
                    type: 'decoded',
                    index,
                    generation,
                    src: image.currentSrc,
                  })
                } catch {
                  dispatch({ type: 'failed', index, generation })
                }
              }}
              onError={() => dispatch({ type: 'failed', index, generation })}
            />
            {permitted &&
              decoded.includes(index) &&
              !mediaFailed.includes(index) &&
              slide.videoSrc && (
                <MotionPortrait
                  index={index}
                  src={slide.videoSrc}
                  poster={posters[index]}
                  running={
                    running &&
                    (index === activeIndex || index === outgoingIndex)
                  }
                  prepare={
                    running && index === nextIndex && stage !== 'waiting'
                  }
                  visible={index === activeIndex || index === outgoingIndex}
                  holdFrame={index !== activeIndex || nextIndex !== null}
                  onStatus={handleMediaStatus}
                />
              )}
          </div>
        )
      })}
      <HeroSignupLink label={signupLinkLabel} />
      {permitted && enhanced && !completed && (
        <button
          type="button"
          className={styles.control}
          onClick={() => setPaused((value) => !value)}
          aria-label={paused ? 'Play hero animation' : 'Pause hero animation'}
          aria-pressed={paused}
          data-hero-motion-control
          data-landing-action="motion_toggle"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            aria-hidden="true"
          >
            {paused ? (
              <path d="M5 2.5v11L13 8z" />
            ) : (
              <>
                <rect x="4" y="3" width="3" height="10" rx="1" />
                <rect x="9" y="3" width="3" height="10" rx="1" />
              </>
            )}
          </svg>
        </button>
      )}
    </div>
  )
}

export default function LandingHeroMedia({
  heroImage,
  slides = landingHeroSlides,
  signupLinkLabel,
}) {
  // Still-review links intentionally never rotate away from the chosen sample.
  if (heroImage)
    return (
      <div className={styles.media} data-hero-media data-hero-phase="preview">
        <Image
          src={heroImage.src}
          alt={heroImage.alt}
          fill
          priority
          sizes={sizes}
        />
        <HeroSignupLink label={signupLinkLabel} />
      </div>
    )
  return (
    <RotatingHero
      key={slides
        .map((slide) => `${slide.id}:${slide.src}:${slide.videoSrc || ''}`)
        .join('|')}
      slides={slides}
      signupLinkLabel={signupLinkLabel}
    />
  )
}
